import React, { useState, useEffect } from "react";
import {
  doc, addDoc, collection, updateDoc, setDoc, deleteDoc, getDoc, getDocs, query, where, arrayUnion,
} from "firebase/firestore";
import { firestore, auth } from "../shared/FirebaseConfig";
import { useUser } from "../shared/UserContext";
import { gradeFields, gradesInSpan, GRADES, studentGrade } from "../shared/gradeModel";
import BulkImport from "./BulkImport";
import AddStudentsHelp, { useAddStudentsHelp } from "./AddStudentsHelp";
import {
  isClassroomConfigured, getClassroomToken, listCourses, listCourseStudents,
} from "../shared/googleClassroom";
import "./AddStudent.css";


// Auto-assigned internal id for "no number" rosters: students are identified by
// name, but every student doc still needs a unique studentId for the data model
// (dedupe, sign-out records). Never shown to a teacher who chose "no number".
const genInternalId = () => `${Date.now()}${Math.floor(Math.random() * 1000)}`;

// ── Shared bulk-save helper ──────────────────────────────────────────
async function saveStudents(validStudents, classId, schoolId, shared) {
  if (!classId || !validStudents.length || !schoolId) return 0;
  const uid = auth.currentUser?.uid;

  // Dedupe scope: shared rosters dedupe school-wide (district IDs are school-unique);
  // private rosters dedupe only within this teacher's own students, so two teachers'
  // class-local numbering (1–24) never collides/merges.
  const rosterSnap = await getDocs(
    shared
      ? query(collection(firestore, "students"), where("schoolId", "==", schoolId))
      : query(collection(firestore, "students"), where("addedBy", "==", uid))
  );
  const rosterById = {};
  const rosterByEmail = {};
  rosterSnap.docs.forEach(d => {
    const data = d.data();
    if (data.studentId) rosterById[data.studentId] = { docId: d.id, ...data };
    const em = (data.schoolEmail || "").trim().toLowerCase();
    if (em) rosterByEmail[em] = { docId: d.id, ...data };
  });

  const newIds = [];
  for (const s of validStudents) {
    // Derive a real student ID when the row has none. Many districts (e.g. SOMSD)
    // encode the SIS number in the school email — "71895@students.somsd.k12.nj.us"
    // → 71895 — so a Google Classroom import (name + email, no ID) still gets the
    // real, roster-matching number instead of an ugly random internal one. Falls
    // back to an internal id only when there's nothing numeric to derive.
    const email = (s.email || "").trim().toLowerCase();
    const emailLocal = email.split("@")[0];
    const derivedId = /^\d{3,}$/.test(emailLocal) ? emailLocal : "";
    const knownId = s.id || derivedId;
    const sid = knownId || genInternalId();
    // Match to an existing kid by ID first, then by school email, so a re-import
    // links to the same student instead of minting a duplicate.
    const existing = (knownId && rosterById[knownId]) || (email && rosterByEmail[email]) || null;
    if (existing) {
      const patch = { enrolledClasses: arrayUnion(classId), teachers: arrayUnion(uid) };
      // Self-heal: if this kid was previously imported with an auto-generated
      // internal id (a 13+ digit timestamp) and we now have a real derived number,
      // upgrade it — so re-importing a Google Classroom course fixes the "weird IDs".
      if (knownId && knownId !== existing.studentId && /^\d{13,}$/.test(existing.studentId || "")) {
        patch.studentId = knownId;
      }
      // Backfill the school email onto a roster entry that has none — e.g. a kid
      // imported from the SIS (ID + grade, no email) now matched from Google
      // Classroom, which carries the email. This is what powers student self-sign-in.
      if (email && !(existing.schoolEmail || "").trim()) {
        patch.schoolEmail = email;
      }
      await updateDoc(doc(firestore, "students", existing.docId), patch);
      newIds.push(existing.docId);
    } else {
      const ref = await addDoc(collection(firestore, "students"), {
        firstName: s.firstName,
        lastName: s.lastName,
        studentName: `${s.firstName} ${s.lastName}`,
        nickname: (s.nickname || "").trim(),
        studentId: sid,
        ...gradeFields(s.grade),
        // School email powers student self-service: a kid signs in with their managed
        // Google account and is matched to THIS roster entry by verified email.
        schoolEmail: (s.email || "").trim().toLowerCase(),
        enrolledClasses: [classId],
        schoolId,
        addedBy: uid || "unknown",
        shared: shared === true,
      });
      newIds.push(ref.id);
    }
  }
  if (newIds.length) {
    await updateDoc(doc(firestore, "classes", classId), {
      enrolledStudents: arrayUnion(...newIds),
    });
  }
  return newIds.length;
}

// Typeahead dropdown of matching directory students (shared by the name + ID fields).
const SuggestionList = ({ items, onPick }) => (
  <div className="as-suggest">
    {items.map((s) => (
      <div key={s.docId} className="as-suggest-item" onMouseDown={() => onPick(s)}>
        <span className="as-suggest-name">{s.studentName}</span>
        <span className="as-suggest-meta">ID {s.studentId} · Gr {studentGrade(s)}</span>
      </div>
    ))}
  </div>
);

// ── Tab: Single ──────────────────────────────────────────────────────
const SingleTab = ({ classId, schoolId, shared, gradeOptions, idScheme, onDone }) => {
  const noId = idScheme === "none"; // name-based roster: hide + auto-assign the ID
  const [firstName, setFirstName]     = useState("");
  const [lastName, setLastName]       = useState("");
  const [nickname, setNickname]       = useState("");
  const [studentId, setStudentId]     = useState("");
  const [grade, setGrade]             = useState("");
  const [schoolEmail, setSchoolEmail] = useState("");
  const [status, setStatus]           = useState(null);
  const [existingDocId, setExistingDocId] = useState(null);
  const [pendingDup, setPendingDup]   = useState(null);
  const [roster, setRoster]           = useState([]);
  const [suggestions, setSuggestions] = useState([]);

  // Load school roster once
  useEffect(() => {
    if (!schoolId) return;
    getDocs(query(collection(firestore, "students"), where("schoolId", "==", schoolId)))
      .then(snap => setRoster(snap.docs.map(d => ({ docId: d.id, ...d.data() }))));
  }, [schoolId]);

  const search = (nameStr, idStr) => {
    const name = nameStr.trim().toLowerCase();
    const id   = idStr.trim().toLowerCase();
    if (!name && !id) { setSuggestions([]); return; }
    const hits = roster.filter(s => {
      if (id) return s.studentId?.toLowerCase().startsWith(id);
      return (
        s.studentName?.toLowerCase().includes(name) ||
        s.firstName?.toLowerCase().startsWith(nameStr.split(" ")[0]?.toLowerCase() || "")
      );
    }).slice(0, 6);
    setSuggestions(hits);
  };

  const handleFirstName = e => {
    const v = e.target.value;
    setFirstName(v); setExistingDocId(null);
    search(`${v} ${lastName}`, "");
  };
  const handleLastName = e => {
    const v = e.target.value;
    setLastName(v); setExistingDocId(null);
    search(`${firstName} ${v}`, "");
  };
  const handleStudentId = e => {
    const v = e.target.value;
    setStudentId(v); setExistingDocId(null);
    search("", v);
  };

  const selectSuggestion = (s) => {
    setFirstName(s.firstName || s.studentName?.split(" ")[0] || "");
    setLastName(s.lastName  || s.studentName?.split(" ").slice(1).join(" ") || "");
    setStudentId(s.studentId || "");
    setGrade(studentGrade(s) || "");
    setExistingDocId(s.docId);
    setSuggestions([]);
  };

  // Do the actual write — link to an existing doc, or create a new one — then reset.
  const finalizeAdd = async (linkDocId) => {
    const uid = auth.currentUser?.uid;
    const addedName = `${firstName} ${lastName}`;
    try {
      if (linkDocId) {
        await updateDoc(doc(firestore, "students", linkDocId), {
          enrolledClasses: arrayUnion(classId),
          teachers: arrayUnion(uid),
        });
        await updateDoc(doc(firestore, "classes", classId), {
          enrolledStudents: arrayUnion(linkDocId),
        });
        setRoster((prev) => prev.map((r) =>
          r.docId === linkDocId
            ? { ...r, enrolledClasses: [...new Set([...(r.enrolledClasses || []), classId])] }
            : r,
        ));
      } else {
        const ref = await addDoc(collection(firestore, "students"), {
          firstName, lastName,
          studentName: addedName,
          nickname: nickname.trim(),
          studentId: noId ? genInternalId() : studentId,
          ...gradeFields(grade),
          schoolEmail: schoolEmail.trim().toLowerCase(),
          enrolledClasses: [classId],
          schoolId,
          addedBy: uid,
          shared: shared === true,
        });
        await updateDoc(doc(firestore, "classes", classId), {
          enrolledStudents: arrayUnion(ref.id),
        });
        setRoster((prev) => [
          ...prev,
          { docId: ref.id, firstName, lastName, studentName: addedName, enrolledClasses: [classId] },
        ]);
      }
      setFirstName(""); setLastName(""); setNickname(""); setStudentId(""); setGrade(""); setSchoolEmail("");
      setExistingDocId(null); setPendingDup(null);
      setStatus({ type: "success", msg: `${addedName} added.` });
      onDone();
    } catch (err) {
      console.error("Error adding student:", err);
      setStatus({ type: "error", msg: "Failed to add student. Try again." });
      setPendingDup(null);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setStatus(null); setPendingDup(null);
    // Safety net against duplicate student docs: an exact school-unique ID match is
    // the SAME kid — link instead of minting a second doc. (School IDs only; class
    // numbering isn't unique.)
    let linkDocId = existingDocId;
    if (!linkDocId && idScheme === "school" && studentId.trim()) {
      const match = roster.find((r) => (r.studentId || "").toLowerCase() === studentId.trim().toLowerCase());
      if (match) linkDocId = match.docId;
    }
    // Name-based guard for schemes we can't auto-merge by ID. Same name already in
    // THIS class → block; same name elsewhere in the school → ask inline (no popup).
    if (!linkDocId) {
      const norm = (s) => (s || "").trim().toLowerCase().replace(/\s+/g, " ");
      const target = norm(`${firstName} ${lastName}`);
      const nameMatches = target
        ? roster.filter((r) => norm(r.studentName || `${r.firstName || ""} ${r.lastName || ""}`) === target)
        : [];
      if (nameMatches.some((r) => (r.enrolledClasses || []).includes(classId))) {
        setStatus({ type: "error", msg: `${firstName} ${lastName} is already in this class.` });
        return;
      }
      if (nameMatches.length > 0) {
        setPendingDup({ docId: nameMatches[0].docId, name: `${firstName} ${lastName}` });
        return;
      }
    }
    finalizeAdd(linkDocId);
  };

  const blurSuggest = () => setTimeout(() => setSuggestions([]), 150);

  return (
    <form onSubmit={handleSubmit} className="as-form">
      <div className="as-field" style={{ marginTop: 0 }}>
        <label>First Name</label>
        <input value={firstName} onChange={handleFirstName} onBlur={blurSuggest} autoComplete="off" required />
        <label style={{ marginTop: 8, display: "block" }}>Last Name</label>
        <input value={lastName} onChange={handleLastName} onBlur={blurSuggest} autoComplete="off" required />
        {suggestions.length > 0 && <SuggestionList items={suggestions} onPick={selectSuggestion} />}
        <label style={{ marginTop: 8, display: "block" }}>
          Nickname <span style={{ fontWeight: 400, color: "var(--color-secondary)" }}>(optional — shown on sign-out)</span>
        </label>
        <input value={nickname} onChange={e => setNickname(e.target.value)} autoComplete="off" placeholder="Goes by…" />
      </div>
      {!noId && (
        <div className="as-field">
          <label>Student ID</label>
          <input value={studentId} onChange={handleStudentId} onBlur={blurSuggest} autoComplete="off" required />
          {suggestions.length > 0 && <SuggestionList items={suggestions} onPick={selectSuggestion} />}
        </div>
      )}
      <div style={{ marginTop: 8 }}>
        <label>Grade <span style={{ fontWeight: 400, color: "var(--color-secondary)" }}>(optional)</span></label>
        <select value={grade} onChange={e => setGrade(e.target.value)}>
          <option value="">No grade</option>
          {gradeOptions.map(g => <option key={g} value={g}>{g}</option>)}
        </select>
      </div>
      {!existingDocId && (
        <div className="as-field">
          <label>School email <span style={{ fontWeight: 400, color: "var(--color-secondary)" }}>(optional — lets them sign in on their own device)</span></label>
          <input
            type="email"
            value={schoolEmail}
            onChange={e => setSchoolEmail(e.target.value)}
            autoComplete="off"
            placeholder="student@school.org"
          />
        </div>
      )}
      {existingDocId && (
        <p style={{ fontSize: 13, color: "var(--color-primary)", margin: "8px 0" }}>
          ✓ Found in school directory — will link to this class.
        </p>
      )}
      {pendingDup && (
        <div className="as-confirm">
          A student named <strong>{pendingDup.name}</strong> is already in your school. Link that
          existing record to this class, or add a separate new student?
          <div className="as-confirm-actions">
            <button type="button" onClick={() => finalizeAdd(pendingDup.docId)}>Link existing student</button>
            <button type="button" className="btn-secondary" onClick={() => finalizeAdd(null)}>Add as new</button>
          </div>
        </div>
      )}
      {status && <p className={`as-status ${status.type}`}>{status.msg}</p>}
      {!pendingDup && <button type="submit" style={{ marginTop: 12 }}>Add student</button>}
    </form>
  );
};

// ── Tab: Bulk Import ─────────────────────────────────────────────────
// Thin wrapper around the shared mapping importer; rows go into THIS class with
// the directory-scope choice made above.
const BulkTab = ({ classId, schoolId, shared, onDone }) => {
  const handleImport = async (students) => {
    const count = await saveStudents(students, classId, schoolId, shared);
    onDone();
    return count;
  };
  return <BulkImport mode="class" onImport={handleImport} />;
};

// ── Tab: Join Code ───────────────────────────────────────────────────
const JoinCodeTab = ({ classId }) => {
  const [code, setCode]       = useState("");
  const [classData, setClassData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCode = async () => {
      if (!classId) return;
      const snap = await getDoc(doc(firestore, "classes", classId));
      const data = snap.exists() ? snap.data() : {};
      setClassData(data);
      setCode(data.enrollmentCode || "");
      setLoading(false);
    };
    fetchCode();
  }, [classId]);

  const generateCode = async () => {
    const newCode = Math.random().toString(36).substring(2, 8).toUpperCase();
    const cls = classData || (await getDoc(doc(firestore, "classes", classId))).data() || {};
    // The `joinCodes/{code}` lookup doc is what lets a not-yet-enrolled student
    // resolve the code → class (the class docs themselves aren't readable to them yet).
    await setDoc(doc(firestore, "joinCodes", newCode), {
      classId,
      schoolId: cls.schoolId || "",
      className: cls.className || "",
      teacherId: auth.currentUser?.uid || cls.teacherId || "",
    });
    if (code) {
      try { await deleteDoc(doc(firestore, "joinCodes", code)); } catch { /* old code may not exist */ }
    }
    await updateDoc(doc(firestore, "classes", classId), { enrollmentCode: newCode });
    setCode(newCode);
  };

  if (loading) return <p>Loading...</p>;

  return (
    <div style={{ maxWidth: 420 }}>
      <p style={{ color: "var(--color-secondary)", marginBottom: 16 }}>
        Share this code so students can join from their own device. They go to{" "}
        <strong>students.classboon.com</strong>, sign in with Google, and enter the code —
        it adds them to this class and turns on their sign-out / attendance / emergency tools.
      </p>
      {code ? (
        <>
          <div className="as-code">{code}</div>
          <button type="button" onClick={generateCode}>Refresh Code</button>
        </>
      ) : (
        <button type="button" onClick={generateCode}>Generate Join Code</button>
      )}
    </div>
  );
};

// ── Tab: Google Classroom ────────────────────────────────────────────
// One-click roster import: connect Google → pick a course → its students (name +
// school email) flow straight into this class via saveStudents. The email carries
// the student-platform sign-in link and makes re-imports dedupe cleanly.
const ClassroomTab = ({ classId, schoolId, shared, onDone }) => {
  const [token, setToken] = useState(null);
  const [courses, setCourses] = useState([]);
  const [courseId, setCourseId] = useState("");
  const [students, setStudents] = useState([]);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(null);

  const connect = async () => {
    setBusy(true); setStatus(null);
    try {
      const t = await getClassroomToken();
      setToken(t);
      const cs = await listCourses(t);
      setCourses(cs);
      if (!cs.length) setStatus({ type: "error", msg: "No active Google Classroom classes found for your account." });
    } catch (e) {
      setStatus({ type: "error", msg: e.message || "Couldn't connect to Google Classroom." });
    } finally { setBusy(false); }
  };

  const pickCourse = async (cid) => {
    setCourseId(cid); setStudents([]); setStatus(null);
    if (!cid) return;
    setBusy(true);
    try {
      const list = await listCourseStudents(token, cid);
      setStudents(list);
      if (!list.length) setStatus({ type: "error", msg: "That class has no students in Google Classroom yet." });
    } catch (e) {
      setStatus({ type: "error", msg: e.message || "Couldn't load that class's students." });
    } finally { setBusy(false); }
  };

  const doImport = async () => {
    if (!students.length) return;
    setBusy(true); setStatus(null);
    try {
      // saveStudents auto-assigns internal IDs and dedupes by school email.
      const rows = students.map((s) => ({ firstName: s.firstName, lastName: s.lastName, email: s.email }));
      const n = await saveStudents(rows, classId, schoolId, shared);
      setStatus({ type: "success", msg: `${n} student${n !== 1 ? "s" : ""} imported from Google Classroom.` });
      setStudents([]); setCourseId("");
      onDone();
    } catch (e) {
      console.error("Classroom import error:", e);
      setStatus({ type: "error", msg: "Import failed. Please try again." });
    } finally { setBusy(false); }
  };

  return (
    <div className="as-panel">
      {!token ? (
        <div className="as-gc-connect">
          <div style={{ fontSize: 34, lineHeight: 1, marginBottom: 8 }}>🎓</div>
          <p>Pull your class roster straight from Google Classroom — names and school emails, no typing.</p>
          <button type="button" onClick={connect} disabled={busy}>
            {busy ? "Connecting…" : "Connect Google Classroom"}
          </button>
        </div>
      ) : (
        <>
          <label style={{ display: "block", fontWeight: "var(--weight-semibold)", marginBottom: 6 }}>
            Choose a Google Classroom class
          </label>
          <select value={courseId} onChange={(e) => pickCourse(e.target.value)} disabled={busy} style={{ minWidth: 280 }}>
            <option value="">Select a class…</option>
            {courses.map((c) => (
              <option key={c.id} value={c.id}>{c.name}{c.section ? ` · ${c.section}` : ""}</option>
            ))}
          </select>
          {students.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <p style={{ fontSize: "var(--text-sm)", margin: "0 0 8px" }}>
                <strong>{students.length}</strong> student{students.length !== 1 ? "s" : ""} ready to import:
              </p>
              <div className="as-gc-preview">
                {students.slice(0, 60).map((s, i) => (
                  <div key={i} className="as-gc-row">
                    <span>{s.firstName} {s.lastName}</span>
                    {s.email && <span className="as-gc-email">{s.email}</span>}
                  </div>
                ))}
                {students.length > 60 && (
                  <div className="as-gc-row"><span>…and {students.length - 60} more</span></div>
                )}
              </div>
              <button type="button" onClick={doImport} disabled={busy}>
                {busy ? "Importing…" : `Import ${students.length} student${students.length !== 1 ? "s" : ""}`}
              </button>
            </div>
          )}
        </>
      )}
      {status && <p className={`as-status ${status.type}`}>{status.msg}</p>}
    </div>
  );
};

// ── Main component ───────────────────────────────────────────────────
// Ordered easiest-first: Google Classroom (one click, when configured) leads,
// then paste-a-list, then single add, then the student self-join code.
const TABS = [
  isClassroomConfigured() && { id: "classroom", label: "Google Classroom", icon: "🎓", badge: "Fastest" },
  { id: "bulk",   label: "Paste a list", icon: "📋" },
  { id: "single", label: "Add one",      icon: "➕" },
  { id: "code",   label: "Join code",    icon: "🔗" },
].filter(Boolean);
const DEFAULT_TAB = isClassroomConfigured() ? "classroom" : "bulk";

// Reusable add-students panel: the Single / Bulk / Join Code tabs + the directory
// option. (Homeroom is a class property now — set it via the checkbox on the
// Classes page, not per student here.) Embedded on the Students page.
export function AddStudentsPanel({ classId, onDone }) {
  const { uid, schoolId, gradeSpan, studentIdScheme } = useUser();
  const gradeOptions = gradeSpan ? gradesInSpan(gradeSpan.low, gradeSpan.high) : GRADES;
  const [activeTab, setActiveTab] = useState(DEFAULT_TAB);
  // Default the directory scope from the teacher's ID scheme: school IDs are
  // school-unique (shared), class numbering / no-number rosters stay private.
  // Unset scheme keeps the historical default (shared).
  const [shared, setShared] = useState(studentIdScheme !== "class" && studentIdScheme !== "none");
  const { showHelp, openHelp, dismissHelp } = useAddStudentsHelp(uid);

  // Is this a specials / pull-out class? Those hold kids who belong to another
  // teacher's homeroom, so they must ALWAYS go into the shared directory (a private
  // copy would double the kid on the Emergency board). We force + lock the directory
  // scope on for them; adding is still allowed (link an existing kid, or create one —
  // written to the directory so a later homeroom teacher / SIS import reconciles by ID).
  const [classType, setClassType] = useState(null);
  useEffect(() => {
    if (!classId) { setClassType(null); return; }
    getDoc(doc(firestore, "classes", classId))
      .then((s) => setClassType(s.exists() ? (s.data().classType || "homeroom") : null))
      .catch((e) => console.error("[AddStudentsPanel] class load:", e));
  }, [classId]);
  const isSpecials = classType === "specials";
  const effectiveShared = isSpecials ? true : shared;

  return (
    <>
      {showHelp && <AddStudentsHelp onClose={dismissHelp} />}

      {/* Header + help */}
      <div className="as-header">
        <h3>Add students</h3>
        <button type="button" className="ash-help-link" onClick={openHelp}>
          How do I add students?
        </button>
      </div>

      {/* Method picker — icon pills, easiest-first */}
      <div className="as-methods">
        {TABS.map(tab => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveTab(tab.id)}
            className={`as-method ${activeTab === tab.id ? "active" : ""}`}
          >
            <span className="as-method-ico">{tab.icon}</span>
            {tab.label}
            {tab.badge && <span className="as-method-badge">{tab.badge}</span>}
          </button>
        ))}
      </div>

      {/* Directory scope — applies to students added via any method. Mandatory for a
          specials/pull-out class (a private copy would double the kid on the Emergency
          board); hidden for "no number" rosters (auto internal IDs, nothing to share). */}
      {activeTab !== "code" && isSpecials && (
        <div className="as-directory-locked">
          <span aria-hidden="true">🔗</span>
          <span>
            <strong>Specials / pull-out class.</strong> Students go into the shared school
            directory, so accounting for one here (e.g. in an emergency) also clears them
            off their homeroom — and roster imports link to the same student instead of duplicating.
          </span>
        </div>
      )}
      {activeTab !== "code" && !isSpecials && studentIdScheme !== "none" && (
        <label className="as-directory">
          <input
            type="checkbox"
            checked={shared}
            onChange={e => setShared(e.target.checked)}
          />
          <span>
            <strong>Add to the school directory</strong>
            <span className="as-directory-hint">
              Lets other teachers sign these students out (art, lunch, nurse). Uncheck only for class-local numbering like 1–24.
            </span>
          </span>
        </label>
      )}

      {/* Tab content */}
      <div style={{ marginBottom: 32 }}>
        {activeTab === "single" && (
          <SingleTab classId={classId} schoolId={schoolId} shared={effectiveShared} gradeOptions={gradeOptions} idScheme={studentIdScheme} onDone={onDone} />
        )}
        {activeTab === "bulk" && (
          <BulkTab classId={classId} schoolId={schoolId} shared={effectiveShared} onDone={onDone} />
        )}
        {activeTab === "classroom" && (
          <ClassroomTab classId={classId} schoolId={schoolId} shared={effectiveShared} onDone={onDone} />
        )}
        {activeTab === "code" && (
          <JoinCodeTab classId={classId} />
        )}
      </div>
    </>
  );
}

